/**
 * Geolocation. One-shot, foreground, on an explicit tap. Never a watch.
 *
 * This is an explicit non-goal of the product, so it is worth stating where the
 * code is rather than only in a document: there is no `watchPosition` anywhere
 * in this app, and nothing ever asks for "always" permission. The two callers
 * are "use my position" on the apply form and the anchor down/up buttons, and
 * both run because a human just pressed something.
 *
 * Anchor-drag alarms are solved by dedicated apps that do it properly and are
 * safety-critical. This app deliberately does not compete with them.
 */

// Distance and the coverage-area check live in @abg/ui/position alongside the
// formatter and parser -- one place that knows about coordinates, not three.
export { distanceM, isNorwegianWaters as looksNorwegian } from '@abg/ui'

export interface Fix {
  lat: number
  lon: number
  accuracyM: number
  at: string
}

export type GeoFailure =
  | { kind: 'unsupported' }
  | { kind: 'denied' }
  | { kind: 'unavailable' }
  | { kind: 'timeout' }
  | { kind: 'insecure' }

export class GeolocationFailed extends Error {
  constructor(readonly failure: GeoFailure) {
    super(describe(failure))
    this.name = 'GeolocationFailed'
  }
}

export function describe(failure: GeoFailure): string {
  switch (failure.kind) {
    case 'unsupported':
      return 'This device cannot provide a position. Enter it on the map instead.'
    case 'denied':
      return 'Location access is off for this app. Enable it in your browser settings, or place the pin on the map.'
    case 'unavailable':
      return 'No position fix available right now. Try again on deck, or place the pin on the map.'
    case 'timeout':
      return 'Getting a fix is taking too long. Try again on deck, or place the pin on the map.'
    case 'insecure':
      return 'Location needs a secure (https) connection.'
  }
}

/**
 * A single fix.
 *
 * `enableHighAccuracy` is on: this is used to record where an anchor went down,
 * and a 3 km cell-tower estimate is not that. The 20 s timeout is generous
 * because a cold GPS start below deck genuinely takes that long, and
 * `maximumAge: 0` refuses a cached fix -- "where am I now" must not be answered
 * with "where you were when you last opened the app".
 */
export function getFix(timeoutMs = 20_000): Promise<Fix> {
  if (!('geolocation' in navigator)) {
    return Promise.reject(new GeolocationFailed({ kind: 'unsupported' }))
  }
  // Browsers refuse geolocation outside a secure context, and the error they
  // give is indistinguishable from a denial. Name it properly.
  if (!window.isSecureContext) {
    return Promise.reject(new GeolocationFailed({ kind: 'insecure' }))
  }
  return new Promise<Fix>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracyM: position.coords.accuracy,
          at: new Date(position.timestamp).toISOString(),
        }),
      (error) => {
        const kind =
          error.code === error.PERMISSION_DENIED
            ? 'denied'
            : error.code === error.TIMEOUT
              ? 'timeout'
              : 'unavailable'
        reject(new GeolocationFailed({ kind }))
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    )
  })
}

/**
 * Has the captain already granted location, without asking them?
 *
 * Used to decide whether "use my position" should be the primary button or a
 * secondary one. The Permissions API is absent on older Safari, which is why
 * this returns 'unknown' rather than assuming.
 */
export async function permissionState(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName })
    return status?.state ?? 'unknown'
  } catch {
    return 'unknown'
  }
}
